// src/context/brandVoice/ui/DocFields.jsx — the body of a document tab: its fields, in
// the order the payload lists them, each as a FieldRow holding the editor its `widget`
// is registered for (⛔ by `widget`, never by the data's shape) or "editor in arrivo".
//
// ⭐ v199: BASE / AVANZATE. `advanced` (the page's toggle, default true = everything, as before) decides what is
// drawn: the tab's base rows, then — only when shown — the advanced rows (advancedModel.isAdvancedField), so
// switching never moves a base field. v200: no column is advanced any more, and the page's toggle (`toggle`) sits
// right after the LAST base row, where the advanced rows appear (it replaces the old "Avanzate" divider). A tab with
// no advanced field draws no toggle.
//
// ⭐ It owns ONE thing of its own: the Freeze / Unlock confirmation. Both write
// immediately and both change what the AI may do to a field, so neither happens on a
// bare click; the page hands down `onFreeze` / `onUnlock` as PROMISES and ConfirmDialog
// closes on resolve. ⛔ A rejection must already carry OUR sentence (`actionErrorCopy`),
// because ConfirmDialog renders the rejection's `message` verbatim — so the page must
// never reject with the raw API error, whose message is the server's English.
//
// Contract, one accessor per field so the page stays thin:
//   fields      — the active tab's `fields[]` entries (payload order)
//   rowFor(f)   — { ctx, value, valueEn, problems, conflict } for that field
//   onChange(f, next) / onChangeEn(f, next) — a WHOLE new value (immutable)
//   onFreeze(f) / onUnlock(f) — Promises; rejection message = our copy
//   onTranslate(f) — "Traduci ora"
//   advanced    — show the advanced rows (default true)
//   toggle      — the "Avanzate" control, drawn after the base rows when the tab has advanced ones
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FieldRow from './FieldRow.jsx';
import BilingualColumns from './BilingualColumns.jsx';
import { WIDGET_EDITORS } from '../widgets/index.js';
import ConfirmDialog from '../../../admin/components/ConfirmDialog.jsx';
import { isAdvancedField } from '../advancedModel.js';
import { COLORS, FONT_SANS } from '../../../constants/theme.js';

const EMPTY_ROW = { ctx: {}, problems: [], conflict: false };

const TOGGLE_ROW = { padding: '16px 0', borderTop: `1px solid ${COLORS.border}`, fontFamily: FONT_SANS };

export default function DocFields({
  fields, rowFor, onChange, onChangeEn, onFreeze, onUnlock, onTranslate, advanced = true, toggle = null,
}) {
  const { t } = useTranslation('context');
  // { kind: 'freeze' | 'unlock', field, label }
  const [pending, setPending] = useState(null);
  const list = Array.isArray(fields) ? fields : [];
  // ⛔ The raw `field_path` is copy ONLY for a field key this release does not know:
  // it keeps a section the backend adds later legible instead of nameless.
  const labelOf = (field) => t(`brandVoice.fields.${field.field_path}`, { defaultValue: field.field_path });

  // ⭐ v186: a bucket-D field ("avoid" list) WITH an editor is shown as two independent
  // columns — the SAME editor for the Italian and for the English half. ⛔ Only when the
  // workspace HAS an English half: without one the backend silently drops `fields_en`, so an
  // English column would collect text that vanishes (then: the single Italian editor, no chip).
  // ⭐ v187: problems are routed PER COLUMN — `onSave` tags those of the English half with
  // `params.lang: 'en'`, so each column marks its own rows. (A backend 422 carries no
  // language: its generic line stays under the field, on no column.)
  const editorFor = (field, row, ctx) => {
    const Editor = WIDGET_EDITORS[field.widget];
    if (!Editor) return null;
    const disabled = !!(ctx.busy || ctx.runLocked);
    const all = row.problems || [];
    const itProblems = all.filter((p) => p?.params?.lang !== 'en');
    const enProblems = all.filter((p) => p?.params?.lang === 'en');
    const italian = (
      <Editor
        field={field}
        lang="it"
        value={row.value}
        valueEn={row.valueEn}
        onChange={(next) => onChange?.(field, next)}
        onChangeEn={(next) => onChangeEn?.(field, next)}
        disabled={disabled}
        problems={itProblems}
      />
    );
    if (field.policy !== 'bucket_d' || ctx.enHalfPresent === false) return italian;
    return (
      <BilingualColumns
        it={italian}
        en={(
          <Editor
            field={field}
            lang="en"
            value={row.valueEn}
            onChange={(next) => onChangeEn?.(field, next)}
            disabled={disabled}
            problems={enProblems}
          />
        )}
      />
    );
  };

  const rows = list.map((field) => ({ field, row: rowFor ? (rowFor(field) || EMPTY_ROW) : EMPTY_ROW }));
  const main = rows.filter(({ field }) => !isAdvancedField(field));
  const extra = rows.filter(({ field }) => isAdvancedField(field));

  const renderRow = ({ field, row }) => {
    const ctx = row.ctx || {};
    const label = labelOf(field);
    return (
      <FieldRow
        key={field.field_path}
        field={field}
        ctx={ctx}
        label={label}
        problems={row.problems}
        conflict={row.conflict}
        onFreeze={(f) => setPending({ kind: 'freeze', field: f, label })}
        onUnlock={(f) => setPending({ kind: 'unlock', field: f, label })}
        onTranslate={onTranslate}
      >
        {editorFor(field, row, ctx)}
      </FieldRow>
    );
  };

  return (
    <div>
      {main.map(renderRow)}

      {toggle && extra.length > 0 && <div style={TOGGLE_ROW}>{toggle}</div>}
      {advanced && extra.map(renderRow)}

      <ConfirmDialog
        open={!!pending}
        onClose={() => setPending(null)}
        onConfirm={() => {
          if (!pending) return undefined;
          return pending.kind === 'freeze' ? onFreeze?.(pending.field) : onUnlock?.(pending.field);
        }}
        title={pending ? t(`brandVoice.actions.${pending.kind}Title`) : ''}
        message={pending ? t(`brandVoice.actions.${pending.kind}Message`, { field: pending.label }) : ''}
        // ⛔ No `confirmLabel`: it would repeat the row's own "Blocca" / "Sblocca per l'IA"
        // word for word while that button is still on screen behind the dialog. The
        // default "Conferma" answers the title, which already names the act.
      />
    </div>
  );
}
